import React, { Component } from "react";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as Cookies from "es-cookie";
import { CommSubTabs } from "../CommSubTabs";
import TemplatesPanel from "../panels/TemplatesPanel";
import WhatsAppLayout from "../../messagesCenter/components/whatsAppLayout";
import SendTemplateToStudentsModal from "./SendTemplateToStudentsModal";
import { parseWaTemplate } from "../../../utils/waTemplateParser";
import { attachModuleGuard, AccessDeniedModal } from "../../../../utils/moduleGuard";
import "../_communicationManagement.scss";

// This tab is the "communication_management" module. Its grant row has an empty
// sub_module_name, so it's checked by module_key only (no sub-module).
const COMMUNICATION_MODULE = ["communication_management"];

const ACCENT = "#25D366";
// Base URL of the rizee backend that serves the WhatsApp REST endpoints. This is
// the SAME backend as GraphQL, so by default derive its origin from
// REACT_APP_API_URL (the per-branch backend the deploy injects, e.g. the prod
// backend on college.rizee.in) by stripping the trailing "/graphql". Explicit
// overrides (REACT_APP_WA_API_BASE / REACT_APP_API_BASE) still win; falls back to
// localhost:3008 only for local dev. This stops the live HTTPS site from calling
// http://localhost (blocked by the browser's "Apps on device" permission).
const WA_API_BASE =
  process.env.REACT_APP_WA_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  (process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/graphql\/?$/, "")
    : "http://localhost:3008");
const SUB_TABS = [
  { key: "messages", label: "Messages", icon: "fal fa-comment-alt" },
  { key: "templates", label: "Templates", icon: "fal fa-file-alt" },
];

// Institute-scoped query (reads c3crm.wa_template directly). The employee-scoped
// getWhatsAppTemplates requires a CRM client_id mapping that not every employee has.
const GET_WA_TEMPLATES = gql`
  query getWaTemplatesByInstitute($institute_id: Int) {
    getWaTemplatesByInstitute(institute_id: $institute_id) {
      id
      type
      template_name
      template_category
      template_lang
      template_form
      template_header
      image_size
      status
    }
  }
`;

class WhatsAppTab extends Component {
  constructor(props) {
    super(props);
    // accessDeniedShow is flipped by the guard when a role without is_edit clicks Send.
    this.state = { activeSubTab: "messages", showStudents: false, accessDeniedShow: false };

    // "Send to Students" is a write. The button stays VISIBLE; the guard intercepts and
    // opens the Access Denied modal when the role lacks is_edit.
    this.guard = attachModuleGuard(this, COMMUNICATION_MODULE);
  }

  handleSubTabClick = (key) => this.setState({ activeSubTab: key });

  // is_edit gate — opens Access Denied and stops before the Send-to-Students modal appears.
  openSendToStudents = () => {
    if (!this.guard.requireEdit()) return;
    this.setState({ showStudents: true });
  };

  getTemplates = () => {
    const q = this.props.waTemplatesQuery || {};
    const raw = q.getWaTemplatesByInstitute || [];
    return raw.map((t) => {
      const parsed = parseWaTemplate(t);
      return {
        id: t.id,
        name: t.template_name || "Untitled",
        category: t.template_category || t.type || "Utility",
        status: t.status || "Pending",
        body: parsed.body || "No preview available for this template.",
        lang: t.template_lang,
        type: t.type,
        headerText: parsed.headerText,
        headerMedia: parsed.headerMedia,
        footer: parsed.footer,
        buttons: parsed.buttons,
        paramCount: parsed.paramCount,
        raw: t,
      };
    });
  };

  renderSubContent = () => {
    if (this.state.activeSubTab === "templates") {
      const q = this.props.waTemplatesQuery || {};
      const templates = this.getTemplates();

      if (q.loading && templates.length === 0) {
        return (
          <div className="text-center text-muted py-5">
            <span className="spinner-border spinner-border-sm me-2" />
            Loading WhatsApp templates…
          </div>
        );
      }
      if (q.error) {
        return (
          <div className="text-center text-danger py-5" style={{ fontSize: 14 }}>
            <i className="fal fa-exclamation-circle me-2" />
            Failed to load templates. Please try again.
          </div>
        );
      }
      if (templates.length === 0) {
        return (
          <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>
            No WhatsApp templates found for this institute.
          </div>
        );
      }
      return (
        <TemplatesPanel channel="WhatsApp" accentColor={ACCENT} templates={templates} />
      );
    }

    // Messages sub-tab: full WhatsApp chat UI (contacts + chat + template send),
    // reused from messagesCenter and pointed at the rizee backend.
    return (
      <div className="comm-wa-chat-embed">
        {/* canSend gates the in-chat "send message" / "send template" actions: it returns
            false and pops the Access Denied modal when the role lacks is_edit. */}
        <WhatsAppLayout apiBase={WA_API_BASE} hideFilters canSend={this.guard.requireEdit} />
      </div>
    );
  };

  render() {
    const institutionId = parseInt(Cookies.get("institutionid") || "0", 10);
    return (
      <div className="comm-mgmt">
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <CommSubTabs
            tabs={SUB_TABS}
            activeKey={this.state.activeSubTab}
            onSelect={this.handleSubTabClick}
          />
          <button
            type="button"
            className="btn btn-sm"
            style={{ background: ACCENT, color: "#fff", fontWeight: 600 }}
            onClick={this.openSendToStudents}
          >
            <i className="fab fa-whatsapp mr-2" />
            Send to Students
          </button>
        </div>
        {this.renderSubContent()}

        <SendTemplateToStudentsModal
          show={this.state.showStudents}
          onClose={() => this.setState({ showStudents: false })}
          templates={this.getTemplates()}
          apiBase={WA_API_BASE}
          institutionId={institutionId}
        />

        {/* Shown when a role without is_edit clicks Send to Students. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default graphql(GET_WA_TEMPLATES, {
  name: "waTemplatesQuery",
  options: () => ({
    variables: { institute_id: parseInt(Cookies.get("institutionid") || "0", 10) },
    fetchPolicy: "cache-and-network",
  }),
})(WhatsAppTab);
